import { ReactNode, useState } from 'react';
import { Upload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { InboxOutlined } from '@ant-design/icons';

const { Dragger } = Upload;

const DEFAULT_ACCEPT = '.pdf,.doc,.docx,.txt,.json,.csv,.xlsx';

type DocumentUploaderProps = Omit<UploadProps, 'action' | 'beforeUpload' | 'customRequest' | 'onChange'> & {
  children?: ReactNode;
  onFilesChange?: (files: File[], fileList: UploadFile[]) => void;
};

const DocumentUploader = ({
  children,
  accept = DEFAULT_ACCEPT,
  multiple = false,
  onFilesChange,
  name = 'file',
  fileList,
  defaultFileList,
  ...rest
}: DocumentUploaderProps) => {
  const [internalFileList, setInternalFileList] = useState<UploadFile[]>(defaultFileList ?? []);
  const isControlled = typeof fileList !== 'undefined';

  const currentFileList = isControlled ? fileList : internalFileList;

  const handleChange = ({ fileList: nextFileList }: UploadChangeParam<UploadFile>) => {
    if (!isControlled) {
      setInternalFileList(nextFileList);
    }

    if (onFilesChange) {
      const files = nextFileList
        .map((item) => item.originFileObj)
        .filter((file): file is NonNullable<typeof file> => file instanceof File) as File[];
      onFilesChange(files, nextFileList);
    }
  };

  const defaultContent = (
    <>
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">Click or drag documents</p>
      <p className="ant-upload-hint">Processed with AI for insights.</p>
    </>
  );

  return (
    <Dragger
      multiple={multiple}
      accept={accept}
      name={name}
      beforeUpload={() => false}
      fileList={currentFileList}
      onChange={handleChange}
      {...rest}
    >
      {children ?? defaultContent}
    </Dragger>
  );
};

export default DocumentUploader;
